import { useTranslation } from "react-i18next";
import { ArrowUpRight } from "lucide-react";
import { useState } from "react";

const VIDEOS = [
  { id: "-XDOc9VONII", key: "video1", cropBars: false },
  { id: "8c2n6tVlrO0", key: "video2", cropBars: true },
  { id: "wap2b4TA2Po", key: "video3", cropBars: true },
] as const;

export default function MediaSection() {
  const { t } = useTranslation();
  const [activeVideos, setActiveVideos] = useState<Record<string, boolean>>({});

  return (
    <section className="relative py-24 lg:py-32 bg-[hsl(var(--dark))] text-white overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage: "url(https://media.lacontessina.com/site/public/images/storage/uploads/2018/01/Pattern-LC.png)",
          backgroundRepeat: "repeat",
        }}
        aria-hidden
      />
      <div className="lc-container-ext relative">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-white/55">
              <span className="h-px w-8 bg-white/30" /> Salone del Mobile
            </span>
            <h2 className="mt-5 font-display font-light tracking-[-0.035em] leading-[1.02] text-[40px] md:text-[64px] lg:text-[80px] max-w-3xl">
              {t("home.section.salone.title").split(" ").slice(0, 2).join(" ")}
              <span className="block italic font-serif font-normal">
                {t("home.section.salone.title").split(" ").slice(2).join(" ")}
              </span>
            </h2>
          </div>
          <p className="max-w-md text-white/65 text-[15px] leading-relaxed">
            {t("home.section.salone.description")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {VIDEOS.map((v) => (
            <div key={v.id} className="group">
              <div className="relative w-full pb-[56.25%] overflow-hidden rounded-[20px] bg-black/40 ring-1 ring-white/10">
                {activeVideos[v.id] ? (
                  <iframe
                    className="absolute inset-0 w-full h-full"
                    src={`https://www.youtube-nocookie.com/embed/${v.id}?autoplay=1`}
                    title={t(`home.section.salone.videos.${v.key}.iframe_title`)}
                    frameBorder={0}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => setActiveVideos((p) => ({ ...p, [v.id]: true }))}
                    aria-label={t(`home.section.salone.videos.${v.key}.iframe_title`)}
                    className="absolute inset-0 w-full h-full group/play cursor-pointer"
                  >
                    <img
                      src={`https://i.ytimg.com/vi/${v.id}/maxresdefault.jpg`}
                      onError={(e) => {
                        const img = e.currentTarget;
                        if (!img.dataset.fallback) {
                          img.dataset.fallback = "1";
                          img.src = `https://i.ytimg.com/vi/${v.id}/mqdefault.jpg`;
                        }
                      }}
                      alt={t(`home.section.salone.videos.${v.key}.iframe_title`)}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover"
                      style={v.cropBars ? { transform: "scale(1.32)", transformOrigin: "center" } : undefined}
                    />
                    <span className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover/play:bg-black/30 transition">
                      <span className="h-16 w-16 rounded-full bg-white/95 flex items-center justify-center shadow-lg">
                        <svg viewBox="0 0 24 24" className="h-7 w-7 ml-1 text-black" fill="currentColor" aria-hidden="true">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </span>
                    </span>
                  </button>
                )}
              </div>
              <div className="mt-5 flex items-start justify-between gap-4">
                <div>
                  <div className="font-serif text-[20px] leading-tight">
                    {t(`home.section.salone.videos.${v.key}.line1`)}
                  </div>
                  <div className="text-[12px] tracking-[0.18em] uppercase text-white/55 mt-1">
                    {t(`home.section.salone.videos.${v.key}.line2`)}
                  </div>
                </div>
                <a
                  href="https://www.youtube.com/@lacontessina/videos"
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label={t("button.watch_more_videos")}
                  className="shrink-0 h-10 w-10 rounded-full border border-white/30 flex items-center justify-center hover:bg-white hover:text-foreground transition"
                >
                  <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
